import React, { useEffect, useState, useMemo } from "react";
import { Flex, Box, Text, Divider, Badge, Image } from "@chakra-ui/react";
import axios from "axios";
import { MessageSquareWarning } from "lucide-react";
import InfoCard from "components/Card/InfoCard";
import PackageReports from "./PackageReports";
import DriverTrackingMap from "./DriverTrackingMap";
import getStatusColor from "../utils/StatusColor";

import { useDispatch, useSelector } from "react-redux";
import { fetchPackageEvidencesAction } from "state/actions/packageEvidenceActions";
import { setClaimFormActivationAction } from "state/actions/claimFormActivationActions";

import GradientButton from "components/Buttons/GradientButton";
import ReusableModal from "components/Modals/ReusableModal";

const ManagePackage = ({ package: pkgData, onClose }) => {
  const dispatch = useDispatch();

  const {
    evidences = [],
    loading: evidenceLoading,
    error: evidenceError,
  } = useSelector((state) => state.packageEvidence || {});

  const { loading: packageLoading } = useSelector((state) => state.packages || {});

  const [routeCoords, setRouteCoords] = useState([]);
  const [eta, setEta] = useState(null);
  const [totalDuration, setTotalDuration] = useState(null);

  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/`.replace(/\/+$/, "/");

  const packageId = pkgData?.original?.package?.package_id;

  const claimRequest = pkgData?.original?.package?.claim_request;

  const [claimFormActivatedLocal, setClaimFormActivatedLocal] = useState(
    pkgData?.original?.package?.claim_form_activated ?? false
  );

  useEffect(() => {
    setClaimFormActivatedLocal(pkgData?.original?.package?.claim_form_activated ?? false);
  }, [pkgData?.original?.package?.claim_form_activated]);

  const handleImageClick = (filePath) => {
    setSelectedImage(filePath);
    setModalOpen(true);
  };

  useEffect(() => {
    if (!packageId) return;
    dispatch(fetchPackageEvidencesAction(packageId));
  }, [dispatch, packageId]);

  const start =
    pkgData?.original?.package?.pickup_lat && pkgData?.original?.package?.pickup_lon
      ? {
          latitude: pkgData.original.package.pickup_lat,
          longitude: pkgData.original.package.pickup_lon,
        }
      : null;

  const destination =
    pkgData?.original?.package?.dropoff_lat && pkgData?.original?.package?.dropoff_lon
      ? {
          latitude: pkgData.original.package.dropoff_lat,
          longitude: pkgData.original.package.dropoff_lon,
        }
      : null;

  const driverLocation =
    pkgData?.original?.delivery?.driver?.driver_lat && pkgData?.original?.delivery?.driver?.driver_lon
      ? {
          latitude: pkgData.original.delivery.driver.driver_lat,
          longitude: pkgData.original.delivery.driver.driver_lon,
        }
      : null;

  const packageStatus = pkgData?.original?.package?.package_status || "Unknown";

  const handleApproveClaimForm = async () => {
    if (!packageId) return;

    const res = await dispatch(setClaimFormActivationAction(packageId, true));
    if (res?.success && res?.data) {
      setClaimFormActivatedLocal(Boolean(res.data.claim_form_activated));
    } else {
      // optional fallback optimistic
      // setClaimFormActivatedLocal(true);
    }
  };

  const handleRejectClaimForm = async () => {
    if (!packageId) return;

    const res = await dispatch(setClaimFormActivationAction(packageId, false));
    if (res?.success && res?.data) {
      setClaimFormActivatedLocal(Boolean(res.data.claim_form_activated));
    } else {
      // optional fallback optimistic
      // setClaimFormActivatedLocal(false);
    }
  };

  useEffect(() => {
    if (!start || !destination) return;

    const fetchRoute = async () => {
      try {
        const response = await axios.get(
          `https://api.mapbox.com/directions/v5/mapbox/driving/${start.longitude},${start.latitude};${destination.longitude},${destination.latitude}`,
          {
            params: {
              access_token: process.env.REACT_APP_MAPBOX_TOKEN,
              geometries: "geojson",
            },
          }
        );

        const route = response.data.routes[0];
        if (route?.geometry?.coordinates?.length) {
          setRouteCoords(
            route.geometry.coordinates.map(([lng, lat]) => ({
              latitude: lat,
              longitude: lng,
            }))
          );
        }
      } catch (err) {
        console.error("Mapbox route error:", err);
      }
    };

    fetchRoute();
  }, [start?.latitude, start?.longitude, destination?.latitude, destination?.longitude]);

  useEffect(() => {
    if (!driverLocation || !destination) return;

    const fetchETA = async () => {
      try {
        const response = await axios.get(
          `https://api.mapbox.com/directions/v5/mapbox/driving/${driverLocation.longitude},${driverLocation.latitude};${destination.longitude},${destination.latitude}`,
          {
            params: {
              access_token: process.env.REACT_APP_MAPBOX_TOKEN,
              geometries: "geojson",
              overview: "simplified",
            },
          }
        );

        const durationValue = response.data.routes[0]?.duration ?? null;
        setEta({ durationText: durationValue, durationValue });
        setTotalDuration((prev) => prev ?? durationValue);
      } catch (err) {
        console.error("ETA fetch error:", err);
      }
    };

    fetchETA();
  }, [driverLocation?.latitude, driverLocation?.longitude, destination?.latitude, destination?.longitude]);

  const formatDuration = (seconds) => {
    if (!seconds) return "Unknown";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    const hrText = hrs === 0 ? "" : hrs === 1 ? "1hr:" : `${hrs}hrs:`;
    return `${hrText}${mins.toString().padStart(2, "0")}min`;
  };

  const currentTrackingState = useMemo(() => {
    if (!pkgData?.original?.package) return {};
    const status = pkgData.original.package.package_status;
    const durationSeconds = eta?.durationValue ?? 0;
    let progress = 0;

    if (totalDuration && durationSeconds != null && ["Picked Up", "In Transit"].includes(status)) {
      const remaining = totalDuration - durationSeconds;
      progress = Math.floor((remaining / totalDuration) * 100);
      progress = Math.max(0, Math.min(100, progress));
    }

    const states = {
      "Awaiting Pick Up": { timeLeft: "0h:00min", progress: 0 },
      "Picked Up": { timeLeft: formatDuration(durationSeconds), progress },
      "In Transit": { timeLeft: formatDuration(durationSeconds), progress },
      Delivered: { timeLeft: "Delivered", progress: 100 },
    };
    return states[status] || {};
  }, [pkgData, eta, totalDuration]);

  const completedCount = Math.floor(((currentTrackingState.progress || 0) / 100) * routeCoords.length);
  const completedRoute = routeCoords.slice(0, completedCount);
  const remainingRoute = routeCoords.slice(completedCount);

  return (
    <Flex direction="column" p={4}>
      <Flex mb={4}>
        <Text fontWeight="500" color="gray.500" fontSize="md">
          Package Status
        </Text>
        <Badge ml={3} colorScheme={getStatusColor(packageStatus)}>
          {packageStatus}
        </Badge>
      </Flex>

      <Box mb={6}>
        <InfoCard
          heading="Claim Form Activation"
          icon={<MessageSquareWarning />}
          headingFontSize="md"
          headingFontWeight="500"
          headingColor="gray.500"
        >
          {!claimRequest ? (
            <Text color="gray.400">No claim request for this package.</Text>
          ) : (
            <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
              <Flex align="center" gap={3}>
                <Text fontSize="sm" color="gray.600" fontWeight="500">
                  Status:
                </Text>

                <Badge
                  colorScheme={claimFormActivatedLocal ? "green" : "red"}
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  {claimFormActivatedLocal ? "Activated" : "Not Activated"}
                </Badge>
              </Flex>

              <Flex gap={3} wrap="wrap">
                <GradientButton
                  width="140px"
                  height="40px"
                  onClick={handleApproveClaimForm}
                  isDisabled={packageLoading || claimFormActivatedLocal === true}
                >
                  Approve
                </GradientButton>

                <GradientButton
                  width="140px"
                  height="40px"
                  onClick={handleRejectClaimForm}
                  isDisabled={packageLoading || claimFormActivatedLocal === false}
                >
                  Reject
                </GradientButton>
              </Flex>
            </Flex>
          )}
        </InfoCard>
      </Box>

      <Divider my={6} />

      {/* EVERYTHING BELOW UNCHANGED (evidence images, tracking, reports, modal) */}
      {/* ...keep your existing code exactly as-is from here down... */}

      {/* Package Evidence by Driver */}
      <Box mb={6}>
        <InfoCard
          heading="Package Evidence by Driver"
          icon={<MessageSquareWarning />}
          headingFontSize="md"
          headingFontWeight="500"
          headingColor="gray.500"
        >
          {evidenceLoading ? (
            <Text color="gray.400">Loading package evidence...</Text>
          ) : evidenceError ? (
            <Text color="red.400">{evidenceError}</Text>
          ) : !Array.isArray(evidences) || evidences.length === 0 ? (
            <Text color="gray.400">No evidence uploaded for this package yet 🚫</Text>
          ) : (
            evidences.map((ev) => (
              <Box key={ev.evidence_id} mb={5}>
                <Text fontSize="sm" color="gray.500">
                  <Text as="span" fontWeight="bold">
                    Uploaded:
                  </Text>{" "}
                  {new Date(ev.created_at).toLocaleString()}
                </Text>

                {ev.comment ? <Text mt={1}>{ev.comment}</Text> : null}

                <Flex mt={3} gap={3} wrap="wrap">
                  {(ev.documents || []).map((doc) => (
                    <Box
                      key={doc.document_id}
                      p={1}
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                      cursor="pointer"
                      onClick={() => handleImageClick(doc.file_path)}
                    >
                      <Image
                        src={`${BASE_URL}${doc.file_path}`}
                        alt="Package evidence"
                        boxSize="90px"
                        objectFit="cover"
                        borderRadius="md"
                        fallbackSrc="https://via.placeholder.com/150?text=Image+Unavailable"
                      />
                    </Box>
                  ))}
                </Flex>
              </Box>
            ))
          )}
        </InfoCard>
      </Box>

      {/* Track Driver */}
      <Box mb={6}>
        <InfoCard
          heading="Track Driver"
          icon={<MessageSquareWarning />}
          headingFontSize="md"
          headingFontWeight="500"
          headingColor="gray.500"
        >
          {start && destination ? (
            <DriverTrackingMap
              driverLocation={driverLocation}
              start={start}
              destination={destination}
              completedRoute={completedRoute}
              remainingRoute={remainingRoute}
              packageStatus={currentTrackingState.state || "Unknown"}
              height={300}
              startColor="#071D54"
              destinationColor="#1454F3"
            />
          ) : (
            <Text color="gray.400">Start or destination unavailable 🚫</Text>
          )}
        </InfoCard>
      </Box>

      {/* Package Reports */}
      <Box mb={6}>
        <InfoCard
          heading="Package Reports"
          icon={<MessageSquareWarning />}
          headingFontSize="md"
          headingFontWeight="500"
          headingColor="gray.500"
        >
          <PackageReports reports={pkgData.mappedReports || []} onUpdateStatus={() => {}} />
        </InfoCard>
      </Box>

      <ReusableModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        bodyMessage={
          selectedImage ? (
            <Image
              src={`${BASE_URL}${selectedImage}`}
              alt="Selected Evidence Image"
              maxH="500px"
              maxW="100%"
              objectFit="contain"
              mx="auto"
              fallbackSrc="https://via.placeholder.com/150?text=Image+Unavailable"
            />
          ) : null
        }
        showConfirmButton={false}
      />
    </Flex>
  );
};

export default ManagePackage;